/**
 * Générateur — Répéter matériellement une expérience aléatoire simple.
 * Enregistrer les résultats observés dans un tableau d'effectifs et de fréquences.
 * Attendu ID : 980 | Niveau : 5ème | Domaine : Probabilités (descripteur 132)
 *
 * Source : doc prof « TANOED EXERCISEUR organisation et gestion des données »,
 * Probabilités cluster 3 (O10). Plan de cartes validé le 2026-07-12.
 * ADAPTATION transverse (signalée) : « ton expérience » / « tes camarades »
 * → séries de résultats FOURNIES (simulées) ; l'expérimentation vécue reste
 * à faire en classe (cœur du cadrage), la version numérique la prolonge.
 *
 * Cartes :
 *   « La pièce de Pedro » (Ex.1 adapté) — 5 × 20 lancers ; les nombres de
 *     PILE sont tirés sous contraintes de divisibilité (cumul 40 pair,
 *     cumul 60 multiple de 3, cumul 80 multiple de 4) → TOUTES les
 *     fréquences cumulées sont des centièmes exacts (aucun arrondi à
 *     scorer, le doc demandait « à 0,01 près »). Premier lot loin de 0,5
 *     (6, 7, 13 ou 14), fréquence finale dans [0,44 ; 0,56] : la
 *     stabilisation est visible par construction. « Place les points » du
 *     doc → QCM de graphiques (ADAPTATION signalée : un placer_points_2d
 *     exigerait un pas de 0,01 → 100 graduations, max 40).
 *   « La simulation du professeur » (Ex.2 — courbes « à reprendre ») —
 *     ADAPTATION signalée : les courbes de fluctuation → diagramme en
 *     barres des fréquences STABILISÉES en % (entiers par construction :
 *     composition × 5). Formule tableur =B2/A2, et le raisonnement inverse
 *     fréquences → composition du sac (20 jetons).
 *   « La somme de deux dés » (Ex.3 — le pont fréquences ↔ probabilités) —
 *     P1 : 20 lancers simulés (ADAPTATION signalée : 14 → 20 lancers pour
 *     des fréquences exactes en %), somme 12 ABSENTE par construction,
 *     nombre de 7 contrôlé. P2 : 1 000 simulations (effectif du 7 multiple
 *     de 10 → % entier). P3 : tableau à double entrée, P(somme) = k/36.
 *     La cascade fréquence observée ≠ fréquence sur 1 000 ≠ probabilité
 *     est posée par trois questions successives.
 *   « Vingt lancers de pièce » (Ex.4 adapté) — série de 20 P/F fournie,
 *     A « obtenir PILE » / B « obtenir FACE » ; double écriture fraction +
 *     décimal (✔ L2) ; nombre de PILE ≠ 10 par construction (le « plus
 *     souvent » est décidable) ; « le hasard n'a pas de mémoire » (21ᵉ
 *     lancer) ; diagramme en bâtons → QCM d'images.
 *   « Trente lancers de dé » (Ex.5 adapté) — 30 résultats fournis,
 *     effectifs des 6 faces DISTINCTS par construction (face majoritaire
 *     unique, QCM d'images discernables) ; C « pair » / D « strictement
 *     supérieur à 4 » avec le distracteur 4 (⚠️ du doc) ; fréquences en
 *     fractions sur 30 (pas de décimal inexact à scorer).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { imgElement, imgChoix, svgBarres, svgLigneBrisee, ligneTable, ligneTableEntete, tableEnonce } from '../statistiques/svgStats';
import { melangeVF } from './svgProbas';

// Phrases à trous en lignes KaTeX globales (audit fractions en texte,
// 2026-09-06) : une fréquence ou une probabilité bâtie autour d'un trou en
// texte brut (« {{9}}/20 ») s'affichait avec sa barre. Chaque ligne est une
// formule entière : texte via \text{}, trou au numérateur avec son étai
// (motif 620). Une ligne par trou, jamais de tiret cadratin entre deux trous.
const T = (s) => `\\text{${s}}`;
const ligneTrou = (avant, trou) => `$${T(avant)} {{${trou}}}$`;
const ligneFrac = (avant, num, den) => `$${T(avant)} \\dfrac{{{${num}}}\\rule[-0.55em]{0pt}{0.55em}}{${den}}$`;

const ATTENDU_ID = 980;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PEDRO = 'La pièce de Pedro';
const L_SIMU = 'La simulation du professeur';
const L_SOMMES = 'La somme de deux dés';
const L_VINGT = 'Vingt lancers de pièce';
const L_TRENTE = 'Trente lancers de dé';

/** c centièmes → "0,65" (sans zéro final). */
const centiFr = (c) => {
    const e = Math.floor(c / 100), r = c % 100;
    return r === 0 ? String(e) : `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`;
};

const fmtMilliers = (n) => (n >= 1000
    ? `${Math.floor(n / 1000)} ${String(n % 1000).padStart(3, '0')}`
    : String(n));

// ═════════════════════════════════════════════════════════════
// CARTE « La pièce de Pedro » (Ex.1 adapté)
// ═════════════════════════════════════════════════════════════

const AMIS = ['Lucien', 'Léonard', 'Louis', 'Sergio'];

/** 5 lots de 20 lancers — cumuls divisibles → fréquences exactes. */
const tirerPedro = () => {
    for (let essai = 0; essai < 120; essai++) {
        const p1 = randomChoice([6, 7, 13, 14]);
        const ps = [p1, randInt(7, 13), randInt(7, 13), randInt(7, 13), randInt(7, 13)];
        const cums = [];
        ps.reduce((acc, p) => { cums.push(acc + p); return acc + p; }, 0);
        if (cums[1] % 2 !== 0 || cums[2] % 3 !== 0 || cums[3] % 4 !== 0) continue;
        if (cums[4] < 44 || cums[4] > 56) continue;
        // fréquences cumulées en centièmes (entières par construction)
        const fcs = [ps[0] * 5, (cums[1] * 5) / 2, (cums[2] * 5) / 3, (cums[3] * 5) / 4, cums[4]];
        return { ps, cums, fcs };
    }
    // Repli déterministe vérifié : 13/11/12/8/8 → cumuls 13, 24, 36, 44, 52
    const ps = [13, 11, 12, 8, 8];
    const cums = [13, 24, 36, 44, 52];
    return { ps, cums, fcs: [65, 60, 60, 55, 52] };
};

const LANCERS_CUM = [20, 40, 60, 80, 100];

const introPedro = (d) =>
    `<p>Pedro lance une pièce 20 fois : il obtient ${d.ps[0]} PILE et ${20 - d.ps[0]} FACE.</p>`;

const introAmis = (d) =>
    `<p>Quatre amis font ensuite chacun 20 lancers avec la même pièce : `
    + AMIS.map((a, i) => `${a} obtient ${d.ps[i + 1]} PILE`).join(', ') + '.</p>';

const tablePedro = (d, masques = []) => tableEnonce(
    ligneTable('Nombre de lancers', LANCERS_CUM),
    ligneTable('Nombre de PILE (cumul)', d.cums.map((c, i) => (masques.includes(i) ? '<strong>?</strong>' : c))),
    ligneTable('Fréquence de PILE', d.fcs.map((f, i) => (masques.includes(i) ? '<strong>?</strong>' : centiFr(f)))),
);

const genPedroTrous = () => {
    const d = tirerPedro();
    if (randInt(0, 1) === 0) {
        const phrase = `${ligneFrac('Fréquence de PILE chez Pedro : ', d.ps[0], 20)}\n`
            + ligneTrou('Soit, en écriture décimale : ', centiFr(d.ps[0] * 5));
        const texte = `${introPedro(d)}<p>Calcule la fréquence de PILE chez Pedro : complète la fraction, `
            + 'puis l\'écriture décimale (ex. : 0,45).</p>';
        return makeTexteTrou(phrase, texte, { ...opts(L_PEDRO), mode: 'saisie', strict: false });
    }
    const [i, j] = shuffle([1, 2, 3, 4]).slice(0, 2).sort((a, b) => a - b);
    const phrase = `Après ${LANCERS_CUM[i]} lancers : {{${d.cums[i]}}} PILE, fréquence {{${centiFr(d.fcs[i])}}} — `
        + `après ${LANCERS_CUM[j]} lancers : {{${d.cums[j]}}} PILE, fréquence {{${centiFr(d.fcs[j])}}}`;
    const texte = `${introPedro(d)}${introAmis(d)}<p>On cumule les résultats au fil des lancers :</p>`
        + tablePedro(d, [i, j])
        + '<p>Complète le tableau (fréquences en écriture décimale, ex. : 0,45).</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_PEDRO), mode: 'saisie', strict: false });
};

const genPedroQcm = () => {
    const d = tirerPedro();
    const variante = randInt(0, 2);
    if (variante === 0) {
        const bonne = 'Non : sur seulement 20 lancers, la fréquence fluctue beaucoup — cela ne prouve rien';
        const choix = [bonne,
            'Oui : une pièce équilibrée donnerait exactement 10 PILE',
            `Oui : ${d.ps[0]} PILE sur 20, c'est ${d.ps[0] > 10 ? 'trop' : 'trop peu'} — la pièce est forcément truquée`,
            'Non : une pièce ne peut jamais être truquée'];
        return makeQcm(choix, bonne,
            `${introPedro(d)}<p>Peut-on en déduire que sa pièce est truquée ?</p>`,
            opts(L_PEDRO));
    }
    if (variante === 1) {
        const bonne = 'La fréquence de PILE se rapproche de 0,5 : rien n\'indique que la pièce soit truquée';
        const choix = [bonne,
            `La fréquence ${d.ps[0] > 10 ? 'augmente' : 'diminue'} régulièrement : la pièce favorise ${d.ps[0] > 10 ? 'PILE' : 'FACE'}`,
            'La pièce est truquée, car la fréquence ne vaut jamais exactement 0,5',
            'On ne peut rien conclure, même avec 100 lancers'];
        return makeQcm(choix, bonne,
            `${introPedro(d)}${introAmis(d)}<p>On cumule les résultats :</p>${tablePedro(d)}`
            + '<p>Que peut-on en déduire pour la pièce ?</p>',
            opts(L_PEDRO));
    }
    // QCM de graphiques (le « place les points » du doc)
    const fabrique = (serie) => imgChoix(svgLigneBrisee(
        LANCERS_CUM.map(String), serie,
        { yStep: 10, yMax: 100, yLabel: 'Fréquence de PILE (%)', xLabel: 'Nombre de lancers' }));
    const bonneSerie = d.fcs;
    const distracteurs = [
        [...d.fcs].reverse(),                      // évolution inversée
        d.fcs.map((f) => 100 - f),                 // fréquence de FACE
        [50, 50, 50, 50, 50],                      // « toujours exactement 0,5 »
    ];
    const bonChoix = fabrique(bonneSerie);
    return makeQcm([bonChoix, ...distracteurs.map(fabrique)], bonChoix,
        `${introPedro(d)}${introAmis(d)}<p>On cumule les résultats :</p>${tablePedro(d)}`
        + '<p>Quel graphique représente l\'évolution de la fréquence de PILE (en %) au fil des lancers ?</p>',
        opts(L_PEDRO));
};

const genPedroVF = () => {
    const d = tirerPedro();
    const vraies = [
        `Sur ses 20 lancers, Pedro obtient plus de ${d.ps[0] > 10 ? 'PILE' : 'FACE'} que de ${d.ps[0] > 10 ? 'FACE' : 'PILE'}.`,
        'Plus le nombre de lancers cumulés augmente, plus la fréquence de PILE se rapproche de 0,5.',
        'La fréquence de PILE change d\'une série de 20 lancers à l\'autre : c\'est la fluctuation d\'échantillonnage.',
    ];
    const fausses = [
        'Une pièce équilibrée donne toujours exactement 10 PILE sur 20 lancers.',
        `Après 100 lancers, on peut affirmer que la pièce est truquée.`,
        `La fréquence de PILE chez Pedro seul est ${centiFr(d.fcs[4])}.`,
    ];
    const lignes = melangeVF(vraies, fausses);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `${introPedro(d)}${introAmis(d)}<p>On cumule les résultats :</p>${tablePedro(d)}<p>Vrai ou faux ?</p>`,
        opts(L_PEDRO));
};

// ═════════════════════════════════════════════════════════════
// CARTE « La simulation du professeur » (Ex.2)
// ═════════════════════════════════════════════════════════════

const JETONS = [
    { sing: 'jaune', plur: 'jaunes' },
    { sing: 'vert', plur: 'verts' },
    { sing: 'rouge', plur: 'rouges' },
    { sing: 'bleu', plur: 'bleus' },
];

/** Composition : 4 effectifs DISTINCTS ≥ 1 sommant à 20 (fréquences ×5 %). */
const tirerSimu = () => {
    const couleurs = shuffle([...JETONS]);
    let compo = null;
    for (let essai = 0; essai < 60 && !compo; essai++) {
        const trois = [randInt(1, 12), randInt(1, 12), randInt(1, 12)];
        const dernier = 20 - trois.reduce((a, b) => a + b, 0);
        const tous = [...trois, dernier];
        if (dernier < 1 || dernier > 12) continue;
        if (new Set(tous).size !== 4) continue;
        compo = tous;
    }
    if (!compo) compo = [10, 5, 4, 1];
    const tirages = randomChoice([500, 1000, 2000]);
    const iRouge = couleurs.findIndex((c) => c.sing === 'rouge');
    const b2 = Math.max(1, Math.min(99, compo[iRouge] * 5 + randInt(-3, 3)));
    return { couleurs, compo, freqs: compo.map((c) => c * 5), tirages, iRouge, b2 };
};

const INTRO_SIMU = '<p>Un sac contient 20 jetons de quatre couleurs. On tire un jeton au hasard, '
    + 'on note sa couleur, puis on le remet dans le sac.</p>';

const elSimu = (s) => imgElement(svgBarres(
    s.couleurs.map((c) => c.plur), s.freqs,
    { yStep: 5, yLabel: 'Fréquence (%)', xLabel: `fréquences stabilisées après ${fmtMilliers(s.tirages)} tirages simulés` },
), 'small');

const tableTableur = (s) => tableEnonce(
    ligneTable('', ['A', 'B', 'C']),
    ligneTable('1', ['nombre de tirages', 'nombre de jetons rouges', 'fréquence des rouges']),
    ligneTable('2', ['100', String(s.b2), '<strong>?</strong>']),
);

const accord = (v, c) => `${v} ${v === 1 ? c.sing : c.plur}`;
const compoTexte = (couleurs, valeurs) =>
    valeurs.slice(0, 3).map((v, i) => accord(v, couleurs[i])).join(', ')
    + ` et ${accord(valeurs[3], couleurs[3])}`;

const genSimuQcm = () => {
    const s = tirerSimu();
    const variante = randInt(0, 2);
    if (variante === 0) {
        const iMax = s.compo.indexOf(Math.max(...s.compo));
        const bonne = `Le ${s.couleurs[iMax].sing}`;
        return {
            ...makeQcm(s.couleurs.map((c) => `Le ${c.sing}`), bonne,
                `${INTRO_SIMU}<p>Le professeur simule ${fmtMilliers(s.tirages)} tirages et représente `
                + 'les fréquences obtenues. Quelle couleur est la plus présente dans le sac ?</p>',
                opts(L_SIMU)),
            elements_additionnels: elSimu(s),
        };
    }
    if (variante === 1) {
        const bonne = '=B2/A2';
        return makeQcm([bonne, '=A2/B2', '=B2×A2', '=B2−A2'], bonne,
            `${INTRO_SIMU}<p>Le professeur note ses résultats dans un tableur :</p>${tableTableur(s)}`
            + '<p>Quelle formule doit-il saisir dans la cellule C2 pour calculer la fréquence des jetons rouges ?</p>',
            opts(L_SIMU));
    }
    const cycle = s.compo.map((_, i) => s.compo[(i + 1) % 4]);
    const bonne = compoTexte(s.couleurs, s.compo);
    const choix = [bonne,
        compoTexte(s.couleurs, s.freqs),          // fréquences prises pour des effectifs (somme 100 ≠ 20)
        compoTexte(s.couleurs, cycle),            // couleurs décalées
        compoTexte(s.couleurs, [...s.compo].reverse())];
    return {
        ...makeQcm(choix, bonne,
            `${INTRO_SIMU}<p>Le professeur simule ${fmtMilliers(s.tirages)} tirages et représente `
            + 'les fréquences obtenues. Le sac contient 20 jetons : quelle composition est la plus vraisemblable ?</p>',
            opts(L_SIMU)),
        elements_additionnels: elSimu(s),
    };
};

const genSimuTrous = () => {
    const s = tirerSimu();
    if (randInt(0, 1) === 0) {
        const phrase = `C2 = B2 ÷ A2 = {{${centiFr(s.b2)}}}`;
        const texte = `${INTRO_SIMU}<p>Le professeur note ses résultats dans un tableur :</p>${tableTableur(s)}`
            + '<p>Calcule la valeur de la cellule C2 (fréquence des jetons rouges sur les 100 premiers tirages), '
            + 'en écriture décimale (ex. : 0,25).</p>';
        return makeTexteTrou(phrase, texte, { ...opts(L_SIMU), mode: 'saisie', strict: false });
    }
    const phrase = s.couleurs.map((c, i) => `${c.plur} : {{${s.compo[i]}}}`).join(' — ');
    const texte = `${INTRO_SIMU}<p>Le professeur simule ${fmtMilliers(s.tirages)} tirages et représente `
        + 'les fréquences obtenues. Le sac contient 20 jetons en tout : utilise les fréquences stabilisées '
        + 'pour retrouver sa composition la plus vraisemblable (nombre de jetons de chaque couleur).</p>';
    return {
        ...makeTexteTrou(phrase, texte, { ...opts(L_SIMU), mode: 'saisie', strict: false }),
        elements_additionnels: elSimu(s),
    };
};

const genSimuVF = () => {
    const s = tirerSimu();
    const iMax = s.compo.indexOf(Math.max(...s.compo));
    const iMin = s.compo.indexOf(Math.min(...s.compo));
    const vraies = [
        `La couleur la plus fréquente est le ${s.couleurs[iMax].sing}.`,
        'Pour estimer la composition du sac, on multiplie chaque fréquence par 20.',
        `La fréquence des jetons ${s.couleurs[iMin].plur} est d'environ ${s.freqs[iMin]} %.`,
    ];
    const fausses = [
        'La formule saisie en C2 est =A2/B2.',
        'La simulation donne la composition EXACTE du sac, à coup sûr.',
        `Le sac contient environ ${s.freqs[iMax]} jetons ${s.couleurs[iMax].plur}.`,
    ];
    const lignes = melangeVF(vraies, fausses);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `${INTRO_SIMU}<p>Le professeur simule ${fmtMilliers(s.tirages)} tirages, note ses résultats `
        + `dans un tableur (fréquence des rouges en C2) et représente les fréquences. Vrai ou faux ?</p>`,
        { ...opts(L_SIMU), elements_additionnels: elSimu(s) });
};

// ═════════════════════════════════════════════════════════════
// CARTE « La somme de deux dés » (Ex.3)
// ═════════════════════════════════════════════════════════════

const PAIRES_7 = [[1, 6], [2, 5], [3, 4], [4, 3], [5, 2], [6, 1]];

/** 20 lancers : k7 sommes 7 (contrôlé), jamais de 12 ; + répartition sur 1000. */
const tirerSommes = () => {
    const k7 = randInt(2, 6);
    const lancers = [];
    for (let i = 0; i < k7; i++) lancers.push(randomChoice(PAIRES_7));
    let garde = 0;
    while (lancers.length < 20 && garde < 500) {
        garde += 1;
        const a = randInt(1, 6), b = randInt(1, 6);
        if (a + b === 7 || (a === 6 && b === 6)) continue;
        lancers.push([a, b]);
    }
    while (lancers.length < 20) lancers.push([2, 3]);   // repli déterministe
    const sommes = shuffle(lancers).map(([a, b]) => a + b);
    // Répartition simulée de 1 000 expériences (le 7 fixé, multiple de 10)
    const e7 = 10 * randInt(15, 19);
    const THEORIQUE = [1, 2, 3, 4, 5, 6, 5, 4, 3, 2, 1];
    const effs1000 = THEORIQUE.map((t) => Math.round((t * 1000) / 36 + randInt(-8, 8)));
    effs1000[5] = e7;
    const diff = 1000 - effs1000.reduce((a, b) => a + b, 0);
    effs1000[4] += Math.trunc(diff / 2);
    effs1000[6] += diff - Math.trunc(diff / 2);
    return { sommes, k7, e7, effs1000 };
};

const INTRO_SOMMES = '<p>On lance deux dés équilibrés et on calcule la SOMME des deux faces.</p>';

const listeSommes = (d) =>
    `<p>Sommes obtenues sur 20 lancers : ${d.sommes.join(' ; ')}.</p>`;

const elSommes = (d) => imgElement(svgBarres(
    ['2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'], d.effs1000,
    { yStep: 20, yLabel: 'Effectif', xLabel: 'somme obtenue (1 000 expériences simulées)' },
), 'small');

const tableSommes = (masques = []) => {
    const lignes = [ligneTableEntete(['+', 1, 2, 3, 4, 5, 6])];
    for (let i = 1; i <= 6; i++) {
        lignes.push(ligneTable(String(i),
            [1, 2, 3, 4, 5, 6].map((j) => (masques.some(([a, b]) => a === i && b === j) ? '<strong>?</strong>' : i + j))));
    }
    return tableEnonce(...lignes);
};

const genSommesQcm = () => {
    const d = tirerSommes();
    const variante = randInt(0, 2);
    if (variante === 0) {
        const bonne = 'Non : la plus petite somme possible est 1 + 1 = 2';
        const choix = [bonne,
            'Oui : il suffit d\'obtenir 1 sur un des deux dés',
            'Non : la plus petite somme possible est 0',
            'Oui : c\'est rare, mais possible'];
        return makeQcm(choix, bonne,
            `${INTRO_SOMMES}${listeSommes(d)}<p>La somme peut-elle être égale à 1 ?</p>`,
            opts(L_SOMMES));
    }
    if (variante === 1) {
        const bonne = 'Oui : il faudrait 6 + 6 — c\'est possible, mais rare';
        const choix = [bonne,
            'Non : si elle n\'apparaît pas dans les 20 lancers, c\'est qu\'elle est impossible',
            'Non : la plus grande somme possible est 11',
            'Oui : elle apparaîtra forcément dans les 20 prochains lancers'];
        return makeQcm(choix, bonne,
            `${INTRO_SOMMES}${listeSommes(d)}<p>La somme 12 n'apparaît pas dans ces 20 lancers. Est-elle possible ?</p>`,
            opts(L_SOMMES));
    }
    const bonne = 'Une seule case du tableau donne la somme 2 (1 + 1), contre six cases pour la somme 7';
    const choix = [bonne,
        'C\'est un hasard propre à cette simulation, une autre donnerait l\'inverse',
        'Les petits nombres sortent moins souvent aux dés',
        'La barre du 2 est mal dessinée'];
    return {
        ...makeQcm(choix, bonne,
            `${INTRO_SOMMES}<p>On simule 1 000 expériences et on représente les effectifs de chaque somme. `
            + `Voici aussi le tableau des sommes :</p>${tableSommes()}`
            + '<p>Pourquoi la barre de la somme 2 est-elle beaucoup plus petite que celle de la somme 7 ?</p>',
            opts(L_SOMMES)),
        elements_additionnels: elSommes(d),
    };
};

const genSommesTrous = () => {
    const d = tirerSommes();
    const variante = randInt(0, 3);
    if (variante === 0) {
        const phrase = `La somme 7 est sortie {{${d.k7}}} fois sur 20 lancers, soit une fréquence de {{${d.k7 * 5}}} %`;
        const texte = `${INTRO_SOMMES}${listeSommes(d)}<p>Compte les sommes égales à 7, puis calcule leur fréquence.</p>`;
        return makeTexteTrou(phrase, texte, { ...opts(L_SOMMES), mode: 'saisie', strict: false });
    }
    if (variante === 1) {
        const phrase = `fréquence de la somme 7 : {{${d.e7 / 10}}} %, soit {{${centiFr(d.e7 / 10)}}} en écriture décimale`;
        const texte = `${INTRO_SOMMES}<p>On simule 1 000 expériences : la somme 7 est sortie ${d.e7} fois.</p>`
            + '<p>Calcule sa fréquence, en pourcentage puis en écriture décimale (ex. : 0,25).</p>';
        return makeTexteTrou(phrase, texte, { ...opts(L_SOMMES), mode: 'saisie', strict: false });
    }
    if (variante === 2) {
        const [o1, o2] = shuffle([0, 1, 2, 3, 4, 5]).slice(0, 2);
        const s1 = 7 + (randInt(0, 1) === 0 ? o1 : -o1);
        const s2 = 7 + (randInt(0, 1) === 0 ? o2 : -o2);
        const phrase = `${ligneFrac(`P(obtenir une somme de ${s1}) = `, 6 - o1, 36)}\n`
            + ligneFrac(`P(obtenir une somme de ${s2}) = `, 6 - o2, 36);
        const texte = `${INTRO_SOMMES}<p>Le tableau donne la somme pour chacun des 36 résultats possibles :</p>`
            + tableSommes()
            + '<p>Compte les cases favorables et complète le numérateur de chaque probabilité (sur 36).</p>';
        return makeTexteTrou(phrase, texte, { ...opts(L_SOMMES), mode: 'saisie', strict: false });
    }
    // Variante « tableau à compléter » (refonte bilan prof D6) : la complétion
    // vérifie la lecture du tableau, le trou final de DÉNOMBREMENT porte le
    // diagnostic (compter un effectif — pont vers la variante 2, P = k/36).
    // Erreurs attendues : « 3 » = ne compte que les cases qu'il vient de
    // remplir ; « 1 » = croit qu'une somme = une seule case ; k±1 = oubli
    // d'une case symétrique de la diagonale.
    const masques = [];
    let garde = 0;
    while (masques.length < 3 && garde < 60) {
        garde += 1;
        const c = [randInt(1, 6), randInt(1, 6)];
        // (1,1) et (6,6) exclues du masquage : leurs sommes (2 et 12) n'ont
        // qu'une case, et la réponse « 1 » serait confusable avec « une case
        // = une somme » — on garantit k ≥ 2 pour la somme dénombrée.
        if (c[0] === c[1] && (c[0] === 1 || c[0] === 6)) continue;
        if (!masques.some(([a, b]) => a === c[0] && b === c[1])) masques.push(c);
    }
    // S tirée parmi les sommes des cases effacées : la question s'ancre dans
    // ce que l'élève vient de produire.
    const [mA, mB] = masques[randInt(0, masques.length - 1)];
    const S = mA + mB;
    const k = 6 - Math.abs(S - 7);
    // Une case par ligne (convention 2026-09-02 : jamais de tiret cadratin
    // entre deux trous indépendants).
    const phrase = masques.map(([a, b]) => `Ligne ${a}, colonne ${b} : {{${a + b}}}`).join('\n')
        + `\nLa somme ${S} apparaît dans {{${k}}} cases sur 36`;
    const texte = `${INTRO_SOMMES}<p>Pour trouver tous les résultats possibles, on construit le tableau à double entrée : `
        + 'chaque ligne donne le résultat du premier dé, chaque colonne celui du second, et chaque case contient la somme. '
        + 'Trois cases sont effacées :</p>'
        + tableSommes(masques)
        + `<p>Complète les trois cases manquantes, puis compte combien de cases du tableau complet donnent la somme ${S}.</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L_SOMMES), mode: 'saisie', strict: false });
};

const genSommesVF = () => {
    const d = tirerSommes();
    const vraies = [
        'Il y a 36 résultats possibles quand on lance deux dés.',
        'P(obtenir une somme de 7) = $\\frac{6}{36}$, soit $\\frac{1}{6}$.',
        'Une seule case du tableau donne la somme 2.',
    ];
    const fausses = [
        'La somme 12 est impossible.',
        'Sur 1 000 lancers, on obtient toujours exactement 167 fois la somme 7.',
        'La somme 1 est possible si un des dés tombe sur 1.',
    ];
    const lignes = melangeVF(vraies, fausses);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `${INTRO_SOMMES}<p>Le tableau donne la somme pour chacun des 36 résultats possibles :</p>${tableSommes()}`
        + '<p>Vrai ou faux ?</p>',
        opts(L_SOMMES));
};

// ═════════════════════════════════════════════════════════════
// CARTE « Vingt lancers de pièce » (Ex.4 adapté)
// ═════════════════════════════════════════════════════════════

const tirerVingt = () => {
    let nP = randInt(7, 13);
    if (nP === 10) nP = randomChoice([9, 11]);       // « plus souvent » décidable
    const serie = shuffle([...Array(nP).fill('P'), ...Array(20 - nP).fill('F')]);
    const autres = [randInt(7, 13), randInt(7, 13), randInt(7, 13)];
    if (autres.every((a) => a === nP)) autres[0] = nP > 9 ? nP - 2 : nP + 2;
    const prenoms = shuffle(['Emma', 'Hugo', 'Nina', 'Tom']).slice(0, 3);
    return { nP, serie, autres, prenoms };
};

const introVingt = (v) =>
    '<p>On lance une pièce 20 fois. A désigne l\'événement « obtenir PILE », B « obtenir FACE ». '
    + `Résultats : ${v.serie.join(' ; ')}.</p>`;

const genVingtTrous = () => {
    const v = tirerVingt();
    const phrase = `${ligneTrou('Effectif de A : ', v.nP)}\n`
        + `${ligneFrac('Fréquence de A : ', v.nP, 20)}\n`
        + `${ligneTrou('Soit, en écriture décimale : ', centiFr(v.nP * 5))}\n`
        + ligneTrou('Effectif de B : ', 20 - v.nP);
    const texte = `${introVingt(v)}<p>Complète les effectifs et les fréquences `
        + '(fréquence en fraction, puis en écriture décimale, ex. : 0,45).</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_VINGT), mode: 'saisie', strict: false });
};

const genVingtQcm = () => {
    const v = tirerVingt();
    const variante = randInt(0, 3);
    if (variante === 0) {
        const bonne = v.nP > 10 ? 'A « obtenir PILE »' : 'B « obtenir FACE »';
        const choix = ['A « obtenir PILE »', 'B « obtenir FACE »', 'A et B à égalité'];
        return makeQcm(choix, bonne,
            `${introVingt(v)}<p>Lequel des deux événements s'est réalisé le plus souvent ?</p>`,
            opts(L_VINGT));
    }
    if (variante === 1) {
        const bonne = 'Non : les résultats varient d\'une série à l\'autre — c\'est la fluctuation d\'échantillonnage';
        const choix = [bonne,
            'Oui : avec la même pièce, on obtient toujours les mêmes résultats',
            'Oui : toute série de 20 lancers donne exactement 10 PILE',
            'Non : certains ont sûrement mal compté'];
        return makeQcm(choix, bonne,
            `${introVingt(v)}<p>Trois camarades font aussi 20 lancers chacun : `
            + v.prenoms.map((p, i) => `${p} obtient ${v.autres[i]} PILE`).join(', ')
            + '.</p><p>Toutes ces séries donnent-elles les mêmes résultats ?</p>',
            opts(L_VINGT));
    }
    if (variante === 2) {
        const bonne = 'On ne peut pas le prévoir : le hasard n\'a pas de mémoire';
        const choix = [bonne,
            'FACE, car elle est « en retard » et doit se rattraper',
            'PILE, car elle est sortie plus souvent jusqu\'ici',
            'Il y aura forcément 10 PILE et 10 FACE à la fin'];
        return makeQcm(choix, bonne,
            `${introVingt(v)}<p>On s'apprête à faire un 21ᵉ lancer. Quel sera le résultat ?</p>`,
            opts(L_VINGT));
    }
    const fabrique = (paire) => imgChoix(svgBarres(['PILE', 'FACE'], paire,
        { yStep: 2, yMax: 20, yLabel: 'Effectif', compact: true }));
    // « autre » doit différer de nP (bonne), 20−nP (inversé) ET 10 (le plat)
    let autre = v.nP + 2;
    if (autre === 20 - v.nP || autre === 10 || autre > 19) autre = v.nP - 2;
    if (autre === 20 - v.nP || autre === 10) autre = v.nP + 3;
    const bonChoix = fabrique([v.nP, 20 - v.nP]);
    const choix = [bonChoix,
        fabrique([20 - v.nP, v.nP]),
        fabrique([10, 10]),
        fabrique([autre, 20 - autre])];
    return makeQcm(choix, bonChoix,
        `${introVingt(v)}<p>Quel diagramme en bâtons représente les effectifs de PILE et de FACE de cette série ?</p>`,
        opts(L_VINGT));
};

const genVingtVF = () => {
    const v = tirerVingt();
    const plus = v.nP > 10 ? 'A « obtenir PILE »' : 'B « obtenir FACE »';
    const vraies = [
        `L'événement ${plus} s'est réalisé le plus souvent dans cette série.`,
        'Les fréquences changent d\'une série de 20 lancers à l\'autre : c\'est la fluctuation d\'échantillonnage.',
        `La fréquence de PILE dans cette série est $\\frac{${v.nP}}{20}$.`,
    ];
    const fausses = [
        'Le 21ᵉ lancer donnera forcément FACE, pour rééquilibrer les résultats.',
        'Comme il y a deux issues, on obtient toujours 10 PILE et 10 FACE.',
        'Quatre séries de 20 lancers donnent toujours exactement le même nombre de PILE.',
    ];
    const lignes = melangeVF(vraies, fausses);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `${introVingt(v)}<p>Vrai ou faux ?</p>`, opts(L_VINGT));
};

// ═════════════════════════════════════════════════════════════
// CARTE « Trente lancers de dé » (Ex.5 adapté)
// ═════════════════════════════════════════════════════════════

/** 6 effectifs DISTINCTS ≥ 2 sommant à 30, C ≠ D en effectif. */
const tirerTrente = () => {
    for (let essai = 0; essai < 120; essai++) {
        const cinq = shuffle([2, 3, 4, 5, 6, 7, 8, 9]).slice(0, 5);
        const dernier = 30 - cinq.reduce((a, b) => a + b, 0);
        if (dernier < 2 || dernier > 11 || cinq.includes(dernier)) continue;
        const effs = shuffle([...cinq, dernier]);
        const effC = effs[1] + effs[3] + effs[5];
        const effD = effs[4] + effs[5];
        if (effC === effD) continue;
        const serie = shuffle(effs.flatMap((e, i) => Array(e).fill(i + 1)));
        return { effs, effC, effD, serie };
    }
    // Repli déterministe vérifié : effC = 7+9+3 = 19 ≠ effD = 5+3 = 8
    const effs = [4, 7, 2, 9, 5, 3];
    return {
        effs, effC: 19, effD: 8,
        serie: effs.flatMap((e, i) => Array(e).fill(i + 1)),
    };
};

const introTrente = (t) =>
    '<p>On lance un dé équilibré 30 fois. C désigne l\'événement « obtenir un nombre pair », '
    + 'D « obtenir un nombre strictement supérieur à 4 ». '
    + `Résultats : ${t.serie.join(' ; ')}.</p>`;

const tableTrente = (t, masques = []) => tableEnonce(
    ligneTable('Face', [1, 2, 3, 4, 5, 6]),
    ligneTable('Effectif', t.effs.map((e, i) => (masques.includes(i) ? '<strong>?</strong>' : e))),
);

const genTrenteTrous = () => {
    const t = tirerTrente();
    if (randInt(0, 1) === 0) {
        const masques = shuffle([0, 1, 2, 3, 4, 5]).slice(0, 3).sort((a, b) => a - b);
        // Une face par ligne (convention 2026-09-02 : jamais de tiret cadratin
        // entre deux trous indépendants).
        const phrase = masques.map((i) => `Face ${i + 1} : {{${t.effs[i]}}}`).join('\n');
        const texte = `${introTrente(t)}<p>On regroupe les résultats dans un tableau, mais trois effectifs `
            + `sont effacés :</p>${tableTrente(t, masques)}<p>Complète les effectifs manquants.</p>`;
        return makeTexteTrou(phrase, texte, { ...opts(L_TRENTE), mode: 'saisie', strict: false });
    }
    const phrase = `${ligneFrac('Fréquence de C, « obtenir un nombre pair » : ', t.effC, 30)}\n`
        + ligneFrac('Fréquence de D, « obtenir un nombre strictement supérieur à 4 » : ', t.effD, 30);
    const texte = `${introTrente(t)}<p>Voici le tableau des effectifs :</p>${tableTrente(t)}`
        + '<p>Complète le numérateur de chaque fréquence (sur 30).</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_TRENTE), mode: 'saisie', strict: false });
};

const genTrenteQcmM = () => {
    const t = tirerTrente();
    const faces = ['1', '2', '3', '4', '5', '6'];
    if (randInt(0, 1) === 0) {
        return makeQcm(faces, ['2', '4', '6'],
            `${introTrente(t)}<p>Coche les <strong>trois</strong> résultats qui réalisent l'événement C `
            + '« obtenir un nombre pair ».</p>',
            opts(L_TRENTE), true);
    }
    return makeQcm(faces, ['5', '6'],
        `${introTrente(t)}<p>Coche les <strong>deux</strong> résultats qui réalisent l'événement D `
        + '« obtenir un nombre STRICTEMENT supérieur à 4 ».</p>',
        opts(L_TRENTE), true);
};

const genTrenteQcmU = () => {
    const t = tirerTrente();
    const variante = randInt(0, 2);
    if (variante === 0) {
        const iMax = t.effs.indexOf(Math.max(...t.effs));
        const autres = shuffle([0, 1, 2, 3, 4, 5].filter((i) => i !== iMax)).slice(0, 3);
        const bonne = `La face ${iMax + 1}`;
        return makeQcm([bonne, ...autres.map((i) => `La face ${i + 1}`)], bonne,
            `${introTrente(t)}<p>Quelle face est apparue le plus souvent ?</p>`,
            opts(L_TRENTE));
    }
    if (variante === 1) {
        const bonne = t.effC > t.effD
            ? 'C « obtenir un nombre pair »'
            : 'D « obtenir un nombre strictement supérieur à 4 »';
        const choix = ['C « obtenir un nombre pair »',
            'D « obtenir un nombre strictement supérieur à 4 »',
            'C et D à égalité'];
        return makeQcm(choix, bonne,
            `${introTrente(t)}<p>Lequel des deux événements s'est réalisé le plus souvent ?</p>`,
            opts(L_TRENTE));
    }
    const fabrique = (effs) => imgChoix(svgBarres(['1', '2', '3', '4', '5', '6'], effs,
        { yStep: 1, yMax: Math.max(...t.effs) + 1, yLabel: 'Effectif', xLabel: 'Face', batons: true, compact: true }));
    const swap = [...t.effs];
    const iMax = t.effs.indexOf(Math.max(...t.effs));
    const iMin = t.effs.indexOf(Math.min(...t.effs));
    [swap[iMax], swap[iMin]] = [swap[iMin], swap[iMax]];
    const cycle = t.effs.map((_, i) => t.effs[(i + 1) % 6]);
    const bonChoix = fabrique(t.effs);
    return makeQcm([bonChoix, fabrique(swap), fabrique(cycle)], bonChoix,
        `${introTrente(t)}<p>Voici le tableau des effectifs :</p>${tableTrente(t)}`
        + '<p>Quel diagramme en bâtons représente ces effectifs ? (Les faces sont dans l\'ordre 1 à 6.)</p>',
        opts(L_TRENTE));
};

const genTrenteVF = () => {
    const t = tirerTrente();
    const iMax = t.effs.indexOf(Math.max(...t.effs));
    const vraies = [
        `L'événement C « obtenir un nombre pair » s'est réalisé ${t.effC} fois.`,
        `La face ${iMax + 1} est celle qui est apparue le plus souvent.`,
        'On a lancé le dé 30 fois en tout.',
    ];
    const fausses = [
        'Le résultat 4 réalise l\'événement D « obtenir un nombre strictement supérieur à 4 ».',
        `La fréquence de C est $\\frac{${t.effD}}{30}$.`,
        'Chaque face apparaît exactement 5 fois sur les 30 lancers.',
    ];
    const lignes = melangeVF(vraies, fausses);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `${introTrente(t)}<p>Voici le tableau des effectifs :</p>${tableTrente(t)}<p>Vrai ou faux ?</p>`,
        opts(L_TRENTE));
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    pedro_texte_trou_saisie: genPedroTrous,
    pedro_qcm_unique: genPedroQcm,
    pedro_tableau_vf: genPedroVF,
    simu_qcm_unique: genSimuQcm,
    simu_texte_trou_saisie: genSimuTrous,
    simu_tableau_vf: genSimuVF,
    sommes_qcm_unique: genSommesQcm,
    sommes_texte_trou_saisie: genSommesTrous,
    sommes_tableau_vf: genSommesVF,
    vingt_texte_trou_saisie: genVingtTrous,
    vingt_qcm_unique: genVingtQcm,
    vingt_tableau_vf: genVingtVF,
    trente_texte_trou_saisie: genTrenteTrous,
    trente_qcm_multiple: genTrenteQcmM,
    trente_qcm_unique: genTrenteQcmU,
    trente_tableau_vf: genTrenteVF,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'pedro', label: L_PEDRO, formatsList: buildFormats('pedro') },
    { id: 'simu', label: L_SIMU, formatsList: buildFormats('simu') },
    { id: 'sommes', label: L_SOMMES, formatsList: buildFormats('sommes') },
    { id: 'vingt', label: L_VINGT, formatsList: buildFormats('vingt') },
    { id: 'trente', label: L_TRENTE, formatsList: buildFormats('trente') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
